import clsx from "clsx";
import { ReactNode } from "react";

export interface DataTableColumn {
  key: string;
  label: ReactNode;
  className?: string;
}

interface DataTableProps {
  dataTestid?: string;
  /**
   * Id of the heading that names this table, for the scroll container's
   * accessible name.
   */
  ariaLabelledBy: string;
  /**
   * Applied to the table itself, not the scroll container - this is where a
   * caller sets the minimum width its columns need before scrolling starts.
   */
  className?: string;
  columns: DataTableColumn[];
  children: ReactNode;
}

const cellClassName =
  "flex min-h-16 min-w-0 items-center px-2 py-2 text-body-base leading-md text-foreground-primary tablet:px-4";

/**
 * Data table: a tinted header row followed by rows whose cells are
 * separated by a bottom divider.
 *
 * Column widths live on the `columns` config and are re-applied to the body
 * cells by `DataTableRow`, so a row only lists its content. Narrower viewports
 * scroll the table horizontally instead of squeezing the columns.
 */
export const DataTable = ({
  dataTestid,
  ariaLabelledBy,
  className,
  columns,
  children,
}: DataTableProps) => (
  // min-w-0: without it the table's minimum width becomes the card's minimum
  // width and pushes the whole page into a horizontal scroll. The scroll
  // container is focusable so the columns stay reachable by keyboard.
  <div
    className="w-full min-w-0 overflow-x-auto"
    role="region"
    aria-labelledby={ariaLabelledBy}
    tabIndex={0}
  >
    <div
      data-testid={dataTestid}
      className={clsx("flex w-full flex-col", className)}
      role="table"
    >
      <div className="flex w-full" role="row">
        {columns.map(({ key, label, className: columnClassName }) => (
          <div
            key={key}
            className={clsx(
              cellClassName,
              "bg-surface-quaternary font-semibold first:rounded-l-md last:rounded-r-md",
              columnClassName,
            )}
            role="columnheader"
          >
            {label}
          </div>
        ))}
      </div>
      {children}
    </div>
  </div>
);

interface DataTableRowProps {
  dataTestid?: string;
  className?: string;
  columns: DataTableColumn[];
  /** If true, the divider is rendered on the last row. */
  withDivider?: boolean;
  /** One node per column, in column order. */
  cells: ReactNode[];
}

export const DataTableRow = ({
  dataTestid,
  className,
  columns,
  withDivider = true,
  cells,
}: DataTableRowProps) => (
  <div
    data-testid={dataTestid}
    className={clsx("flex w-full", className)}
    role="row"
  >
    {columns.map(({ key, className: columnClassName }, index) => (
      <div
        key={key}
        className={clsx(
          cellClassName,
          withDivider && "border-b border-border-tertiary",
          columnClassName,
        )}
        role="cell"
      >
        {cells[index]}
      </div>
    ))}
  </div>
);
